import * as React from 'react';
import { useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Alert } from 'react-bootstrap';

import { EnergyPlanSnapshot, SolarState } from '../../common';
import { LayoutSize, layoutFor, useInterval, useViewportWidth } from '../util';
import { energyMockSnapshot, energyMockSolarState, legacySnapshot } from '../util/energy_mock';
import { AirRoom, VENT_NAMES, airRooms, ventLevelNow } from '../util/air';
import {
  ACTIONS,
  ActionRun,
  HOUR_MS,
  PLAN_STALE_AFTER_MS,
  SLOT_MS,
  buildRows,
  buildRuns,
  euros,
  flowNow,
  monthName,
  noExportBlocks,
} from '../util/energy_model';
import { capitalize, dayKey, hhmm, num1 } from '../util/format';
import { useHeaderStatus } from '../util/header_status';
import { formatRelative, formatTallinnDayTime } from '../util/tallinn_time';
import { PowerFlowCard } from './energy_flow';
import { TimelineCard } from './energy_timeline';
import { AirRoomRow } from './ui/air';
import { Card, chipStyle, Dot, dotStyle, pageStyle } from './ui/card';
import { HeaderStatus } from './ui/header_status';
import {
  ALERT,
  CARD_BG,
  CARD_SHADOW,
  DOT,
  HAIRLINE,
  HAIRLINE_STRONG,
  INK,
  MUTED,
  SAVING_GREEN,
  SECONDARY,
  VENT_COLOR,
  WARN,
} from './ui/tokens';

const COMING_UP_MS = 12 * HOUR_MS;
// Beside the cost / air cards (web, landscape iPad): more runs scroll inside the card instead of stretching
// the row.
const COMING_UP_VISIBLE = 5;

// `?mock=energy` previews the tab with generated data (`?mock=energy-old`: without the fields older
// controllers don't send). `&at=2026-01-13T14:20:00%2B02:00` pins the mock clock, e.g. for screenshots.
interface MockConfig {
  legacy: boolean;
  clockOffsetMs: number;
}

function mockConfig(): MockConfig | undefined {
  const params = new URLSearchParams(window.location.search);
  const mock = params.get('mock');
  if (mock !== 'energy' && mock !== 'energy-old') {
    return undefined;
  }
  const at = Date.parse(params.get('at') ?? '');
  return { legacy: mock === 'energy-old', clockOffsetMs: Number.isNaN(at) ? 0 : at - Date.now() };
}

// ---- Cost -------------------------------------------------------------------------------------------

interface CostColumn {
  label: string;
  value: string;
  sub?: string;
  subColor?: string;
  note?: string;
  title?: string;
}

function CostCard(props: { plan: EnergyPlanSnapshot; size: LayoutSize }) {
  const { plan, size } = props;
  const cols: CostColumn[] = [];
  const horizonH =
    plan.slots.length > 0 ? (plan.slots[plan.slots.length - 1].start + SLOT_MS - plan.slots[0].start) / HOUR_MS : 0;
  // Net of the battery left at the horizon end when the controller sends it: a plan that buys cheap night
  // energy for the next morning spends more inside the horizon but ends with a fuller battery.
  const net = plan.netCostCents !== undefined && plan.selfUseNetCostCents !== undefined;
  const saving = net ? plan.selfUseNetCostCents! - plan.netCostCents! : plan.selfUseCostCents - plan.totalCostCents;
  const lastSlot = plan.slots[plan.slots.length - 1];
  cols.push({
    label: `Next ${horizonH.toFixed(0)} h`,
    value: euros(plan.totalCostCents),
    sub: saving >= 0 ? `saves ${euros(saving)}` : `costs ${euros(-saving)} more`,
    subColor: saving >= 0 ? SAVING_GREEN : ALERT.strong,
    title: net
      ? `Planned spend ${euros(plan.totalCostCents)} vs ${euros(plan.selfUseCostCents)} on plain Self Use. ` +
        `The plan ends at ${Math.round(lastSlot?.socEndPercent ?? 0)} % battery, Self Use at ` +
        `${Math.round(plan.selfUseEndSocPercent ?? 0)} %; counting what that leftover energy is worth, ` +
        `the plan ${saving >= 0 ? 'saves' : 'costs'} ${euros(Math.abs(saving))}.`
      : `Planned cost vs ${euros(plan.selfUseCostCents)} on plain Self Use`,
  });
  const y = plan.yesterday;
  const yCost = y?.gridCostCents ?? y?.actualCostCents;
  if (y && yCost !== undefined) {
    cols.push({
      label: 'Yesterday',
      value: euros(yCost),
      sub: y.savingVsSelfUseCents !== undefined ? `saved ${euros(y.savingVsSelfUseCents)}` : undefined,
      title:
        (y.gridCostCents !== undefined
          ? 'Grid energy incl VAT and per-kWh fees, export credited'
          : 'Incl battery wear') + (y.completeness < 0.9 ? ` · data ${Math.round(y.completeness * 100)} %` : ''),
    });
  }
  const m = plan.monthToDate;
  let footnote: string | undefined;
  if (m) {
    footnote = `grid energy + fees incl VAT, export credited, monthly fees included · ${m.daysCovered} of ${m.daysInPeriod} days measured`;
    const incomplete = m.daysCovered < m.daysInPeriod || m.completeness < 0.9;
    cols.push({
      label: `${monthName(m.month)} so far`,
      value: euros(m.costCents),
      sub: m.savingVsSelfUseCents !== undefined ? `saved ${euros(m.savingVsSelfUseCents)}` : undefined,
      note: incomplete && size !== 'web' ? `${m.daysCovered} of ${m.daysInPeriod} days` : undefined,
      title: footnote.charAt(0).toUpperCase() + footnote.slice(1),
    });
  }
  const phone = size === 'phone';
  return (
    <Card
      label="Cost"
      className="num"
      radius={16}
      style={{
        padding: phone ? '10px 12px' : '12px 14px',
        display: 'grid',
        gridTemplateColumns: `repeat(${cols.length}, minmax(0, 1fr))`,
        gap: 10,
      }}
    >
      {cols.map((c, i) => (
        <span
          key={c.label}
          title={c.title}
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: phone ? 1 : 2,
            fontSize: 11,
            color: SECONDARY,
            minWidth: 0,
            paddingLeft: i > 0 ? 10 : 0,
            borderLeft: i > 0 ? `1px solid ${HAIRLINE}` : undefined,
          }}
        >
          <span style={{ whiteSpace: 'nowrap' }}>{c.label}</span>
          <b style={{ fontSize: phone ? 15 : 17, color: INK }}>{c.value}</b>
          {c.sub && <b style={{ color: c.subColor ?? SAVING_GREEN, whiteSpace: 'nowrap' }}>{c.sub}</b>}
          {c.note && <span style={{ color: MUTED }}>{c.note}</span>}
        </span>
      ))}
      {size === 'web' && footnote && (
        <span
          style={{
            gridColumn: '1 / -1',
            fontSize: 11,
            color: MUTED,
            paddingTop: 6,
            borderTop: `1px solid ${HAIRLINE}`,
          }}
        >
          Month: {footnote}
        </span>
      )}
    </Card>
  );
}

// ---- No-export banner -------------------------------------------------------------------------------

function NoExportBanner(props: { plan: EnergyPlanSnapshot; now: number; size: LayoutSize }) {
  const b = noExportBlocks(props.plan, props.now);
  if (!b) {
    return null;
  }
  const lead = props.size === 'phone' ? 'price to' : props.size === 'tablet' ? 'to' : 'export price to';
  return (
    <div
      role="status"
      className="num"
      style={{
        display: 'flex',
        gap: 8,
        alignItems: 'center',
        background: ALERT.bg,
        color: ALERT.fg,
        borderRadius: 12,
        padding: '9px 12px',
        fontSize: 13,
      }}
    >
      <svg
        width="16"
        height="16"
        viewBox="0 0 24 24"
        fill="none"
        stroke={ALERT.strong}
        strokeWidth="2.2"
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden="true"
        style={{ flex: 'none' }}
      >
        <path d="M7 22L12 2l5 20M5 8h14" />
        <path d="M3 3l18 18" />
      </svg>
      <span>
        <b>No export {b.when}</b> · {lead} {num1(b.lowestExportPrice)} c
      </span>
    </div>
  );
}

// ---- Air & ventilation ------------------------------------------------------------------------------

function AirChips(props: { rooms: AirRoom[]; vent?: string }) {
  return (
    <div aria-label="Air and ventilation" style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
      {props.rooms.map((r) => (
        <span key={r.name} className="num" aria-label={`${r.name} CO2 ${r.co2} ppm, ${r.status}`} style={chipStyle}>
          <Dot color={r.dot} />
          {r.name} <b>{r.co2}</b>
        </span>
      ))}
      {props.vent && (
        <span style={chipStyle}>
          Vent <b>{props.vent}</b>
        </span>
      )}
    </div>
  );
}

function AirCard(props: { rooms: AirRoom[]; vent?: string }) {
  return (
    <Card
      label="Air and ventilation"
      radius={16}
      style={{
        flex: 1,
        justifyContent: 'center',
        padding: '12px 14px',
        display: 'flex',
        flexDirection: 'column',
        gap: 8,
      }}
    >
      {props.rooms.map((r) => (
        <AirRoomRow key={r.name} room={r} />
      ))}
      {props.vent && (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            fontSize: 13,
            paddingTop: props.rooms.length > 0 ? 8 : 0,
            borderTop: props.rooms.length > 0 ? `1px solid ${HAIRLINE}` : undefined,
          }}
        >
          <span style={{ width: 8, height: 8, borderRadius: 2, background: VENT_COLOR }} />
          <span style={{ flex: 1 }}>Ventilation</span>
          <b>{props.vent}</b>
        </div>
      )}
    </Card>
  );
}

// ---- Coming up --------------------------------------------------------------------------------------

interface Upcoming {
  key: number;
  time: string;
  label: string;
  color: string;
  soc: string;
  importKwh: number;
  exportKwh: number;
  avgPrice: number;
  estimated: boolean;
  noExport: boolean;
}

function upcomingRuns(runs: ActionRun[], now: number): Upcoming[] {
  return runs
    .filter((r) => !r.past && r.end > now && r.start < now + COMING_UP_MS)
    .map((r) => {
      let importKwh = 0;
      let exportKwh = 0;
      for (const row of r.rows) {
        const g = row.gridKwh ?? 0;
        if (g > 0) importKwh += g;
        else exportKwh -= g;
      }
      const first = r.rows[0];
      const last = r.rows[r.rows.length - 1];
      return {
        key: r.start,
        time: `${hhmm(r.start)}–${hhmm(r.end)}`,
        label: ACTIONS[r.action].label,
        color: ACTIONS[r.action].bar,
        soc: `${Math.round(first.socStartPercent ?? 0)}→${Math.round(last.socEndPercent ?? 0)}%`,
        importKwh,
        exportKwh,
        avgPrice: r.rows.reduce((sum, row) => sum + (row.importPrice ?? 0), 0) / r.rows.length,
        estimated: r.rows.some((row) => row.estimatedPrice),
        noExport: r.rows.some((row) => row.curtailExport),
      };
    });
}

const priceText = (u: Upcoming) => `${u.estimated ? '~' : ''}${u.avgPrice.toFixed(1)}`;

// `scrollBeside`: scroll past COMING_UP_VISIBLE runs while the card sits in a row with others (not once the
// row has wrapped it onto a line of its own); `width` (viewport) re-checks that on resize.
function ComingUpList(props: {
  items: Upcoming[];
  size: LayoutSize;
  scrollBeside?: boolean;
  width?: number;
  style?: React.CSSProperties;
}) {
  const web = props.size === 'web';
  const maxAvg = Math.max(1, ...props.items.map((u) => u.avgPrice));
  // Height of the first COMING_UP_VISIBLE rows (they differ: a "no export" line can wrap).
  const listRef = useRef<HTMLDivElement>(null);
  const [maxHeight, setMaxHeight] = useState<number | undefined>(undefined);
  useLayoutEffect(() => {
    const card = listRef.current?.parentElement;
    const first = card?.parentElement?.firstElementChild as HTMLElement | null | undefined;
    const beside = !!card && !!first && first !== card && first.offsetTop === card.offsetTop;
    const last = listRef.current?.children[COMING_UP_VISIBLE - 1] as HTMLElement | undefined;
    setMaxHeight(
      props.scrollBeside && beside && props.items.length > COMING_UP_VISIBLE && last
        ? last.offsetTop + last.offsetHeight
        : undefined,
    );
  }, [props.items, props.scrollBeside, props.width]);
  return (
    <Card
      label="Coming up"
      radius={web ? 16 : 20}
      style={{
        padding: web ? '12px 14px 4px' : '14px 14px 6px',
        display: 'flex',
        flexDirection: 'column',
        ...props.style,
      }}
    >
      <div
        style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', paddingBottom: web ? 4 : 6 }}
      >
        <span style={{ fontSize: 14, fontWeight: 600 }}>Coming up</span>
        <span style={{ fontSize: 12, color: SECONDARY }}>next 12 h</span>
      </div>
      <div ref={listRef} style={{ position: 'relative', maxHeight, overflowY: maxHeight ? 'auto' : undefined }}>
        {props.items.map((u) => (
          <div
            key={u.key}
            style={{
              display: 'grid',
              gridTemplateColumns: `${web ? 5 : 6}px minmax(0, 1fr) auto`,
              gap: web ? 10 : 12,
              alignItems: 'center',
              padding: web ? '8px 0' : '10px 0',
              borderTop: `1px solid ${HAIRLINE}`,
            }}
          >
            <span style={{ alignSelf: 'stretch', borderRadius: 3, background: u.color }} />
            <div style={{ display: 'flex', flexDirection: 'column', gap: web ? 1 : 2, minWidth: 0 }}>
              <span className="num" style={{ fontSize: web ? 13 : 14, fontWeight: 600 }}>
                {u.time} <span style={{ fontWeight: 400, color: SECONDARY }}>· {u.label}</span>
              </span>
              <span className="num" style={{ fontSize: web ? 11 : 12, color: SECONDARY }}>
                Battery {u.soc} · {u.importKwh.toFixed(1)} kWh {web ? 'in' : 'from grid'}
                {u.exportKwh > 0.05 ? ` · ${u.exportKwh.toFixed(1)} kWh ${web ? 'out' : 'exported'}` : ''}
                {u.noExport && <span style={{ color: ALERT.strong, fontWeight: 600 }}> · no export</span>}
              </span>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 4 }}>
              <span className="num" style={{ fontSize: web ? 13 : 14, fontWeight: 600, whiteSpace: 'nowrap' }}>
                {priceText(u)} <span style={{ fontSize: 11, fontWeight: 400, color: SECONDARY }}>c</span>
              </span>
              {!web && (
                <span
                  style={{
                    width: 56,
                    height: 4,
                    borderRadius: 2,
                    background: '#e3eefb',
                    position: 'relative',
                    overflow: 'hidden',
                  }}
                >
                  <span
                    style={{
                      position: 'absolute',
                      left: 0,
                      top: 0,
                      bottom: 0,
                      width: `${Math.round((u.avgPrice / maxAvg) * 100)}%`,
                      background: '#2a78d6',
                    }}
                  />
                </span>
              )}
            </div>
          </div>
        ))}
      </div>
    </Card>
  );
}

function ComingUpCards(props: { items: Upcoming[] }) {
  return (
    <section aria-label="Coming up" style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', padding: '0 4px' }}>
        <span style={{ fontSize: 15, fontWeight: 600 }}>Coming up</span>
        <span style={{ fontSize: 12, color: SECONDARY }}>next 12 h</span>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, minmax(0, 1fr))', gap: 12 }}>
        {props.items.slice(0, 4).map((u) => (
          <div
            key={u.key}
            style={{
              background: CARD_BG,
              borderRadius: 16,
              padding: '12px 14px',
              display: 'flex',
              flexDirection: 'column',
              gap: 6,
              boxShadow: CARD_SHADOW,
            }}
          >
            <span style={{ height: 4, borderRadius: 2, background: u.color }} />
            <span className="num" style={{ fontSize: 14, fontWeight: 600 }}>
              {u.time}
            </span>
            <span style={{ fontSize: 13, color: SECONDARY }}>{u.label}</span>
            <span className="num" style={{ fontSize: 12, color: SECONDARY }}>
              avg <b style={{ color: INK }}>{priceText(u)} c</b> · {u.soc}
            </span>
            {(u.importKwh > 0.05 || u.exportKwh > 0.05) && (
              <span className="num" style={{ fontSize: 12, color: SECONDARY }}>
                {u.importKwh.toFixed(1)} kWh in
                {u.exportKwh > 0.05 ? ` · ${u.exportKwh.toFixed(1)} out` : ''}
              </span>
            )}
            {u.noExport && <span style={{ fontSize: 11, fontWeight: 600, color: ALERT.strong }}>no export</span>}
          </div>
        ))}
      </div>
    </section>
  );
}

// ---- Header status, footer --------------------------------------------------------------------------

// "● live · plan 4 min ago" (+ when it was made, the modes and the config hash where there is room).
function statusText(stale: boolean, live: boolean, age: string, made: string, modes: string, cfg?: string) {
  const lead = `${stale ? 'stale · ' : live ? 'live · ' : ''}plan ${age}`;
  return {
    phone: lead,
    tablet: [capitalize(`${lead} · ${made}`), capitalize(modes).replace(' · vent', ' · Vent')] as [string, string],
    web: [`${lead} (${made})`, modes, ...(cfg ? [<span style={{ color: MUTED }}>cfg {cfg}</span>] : [])] as [
      string,
      ...React.ReactNode[],
    ],
  };
}

function Footer(props: { plan: EnergyPlanSnapshot; size: LayoutSize; modes: string }) {
  const { plan, size } = props;
  const parts: string[] = [];
  if (size === 'phone') {
    parts.push(`Plan made ${hhmm(plan.createdAt)}`, props.modes);
  }
  if (size !== 'web' && plan.configVersion) {
    parts.push(`cfg ${plan.configVersion.slice(0, 8)}`);
  }
  if (plan.ventManualOverrideSince !== undefined) {
    parts.push('vent manual override');
  }
  const cw = plan.chargeWindows;
  if (cw?.gridChargeEnabled && cw.windows.length > 0) {
    parts.push(`charge window ${cw.windows.map((w) => `${w.start}–${w.end}`).join(', ')} → ${cw.targetSocPercent} %`);
  }
  if (parts.length === 0) {
    return null;
  }
  return (
    <p className="num" style={{ margin: 0, fontSize: 11, color: MUTED }}>
      {parts.join(' · ')}
    </p>
  );
}

// ---- Alerts -----------------------------------------------------------------------------------------

const ALERTS_COLLAPSED = 3;

const alertToggle = (color: string): React.CSSProperties => ({
  border: 0,
  background: 'transparent',
  minHeight: 36,
  padding: '0 0 0 16px',
  fontSize: 12,
  fontWeight: 600,
  color,
  cursor: 'pointer',
});

// The controller's alerts of the last 24 h, newest first, at the top of the page. Open ones in the amber
// banner; resolved ones (the condition is over) greyed out behind a toggle until they age out.
function AlertsBanner(props: { plan: EnergyPlanSnapshot; now: number }) {
  const [expanded, setExpanded] = useState(false);
  const [showResolved, setShowResolved] = useState(false);
  const alerts = [...(props.plan.alerts ?? [])].sort((a, b) => b.timestamp - a.timestamp);
  if (alerts.length === 0) {
    return null;
  }
  const open = alerts.filter((a) => a.resolvedAt === undefined);
  const resolved = alerts.filter((a) => a.resolvedAt !== undefined);
  const shown = expanded ? open : open.slice(0, ALERTS_COLLAPSED);
  const today = dayKey(props.now);
  const when = (ts: number) => (dayKey(ts) === today ? hhmm(ts) : formatTallinnDayTime(ts));
  const resolvedRows =
    showResolved &&
    resolved.map((a, i) => (
      <div key={`r${i}`} style={{ display: 'flex', gap: 8, alignItems: 'flex-start', color: MUTED }}>
        <span aria-hidden="true" style={{ ...dotStyle(HAIRLINE_STRONG), marginTop: 5 }} />
        <span style={{ minWidth: 0 }}>
          {when(a.timestamp)} <b style={{ fontWeight: 600 }}>{a.kind}</b>: {a.message} · resolved {when(a.resolvedAt!)}
        </span>
      </div>
    ));

  if (open.length === 0) {
    return (
      <section
        aria-label="Resolved alerts"
        className="num"
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: 4,
          background: CARD_BG,
          color: MUTED,
          borderRadius: 12,
          padding: showResolved ? '9px 12px 0' : '0 12px',
          fontSize: 13,
          boxShadow: CARD_SHADOW,
        }}
      >
        {resolvedRows}
        <button
          type="button"
          onClick={() => setShowResolved(!showResolved)}
          style={{ ...alertToggle(MUTED), alignSelf: 'flex-start', padding: 0 }}
        >
          {showResolved
            ? 'Hide resolved alerts'
            : `${resolved.length} resolved alert${resolved.length === 1 ? '' : 's'} in the last 24 h`}
        </button>
      </section>
    );
  }

  return (
    <section
      aria-label="Alerts"
      role="alert"
      className="num"
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: 4,
        background: WARN.bg,
        color: WARN.fg,
        borderRadius: 12,
        padding: '9px 12px',
        fontSize: 13,
        boxShadow: '0 0 0 1px rgba(171,112,0,0.18)',
      }}
    >
      {shown.map((a, i) => (
        <div key={i} style={{ display: 'flex', gap: 8, alignItems: 'flex-start' }}>
          <span aria-hidden="true" style={{ ...dotStyle(DOT.warning), marginTop: 5 }} />
          <span style={{ minWidth: 0 }}>
            <span style={{ color: WARN.strong }}>{when(a.timestamp)}</span> <b>{a.kind}</b>: {a.message}
          </span>
        </div>
      ))}
      {resolvedRows}
      {(open.length > ALERTS_COLLAPSED || resolved.length > 0) && (
        <div style={{ display: 'flex', flexWrap: 'wrap' }}>
          {open.length > ALERTS_COLLAPSED && (
            <button type="button" onClick={() => setExpanded(!expanded)} style={alertToggle(WARN.strong)}>
              {expanded ? 'Show fewer' : `${open.length - ALERTS_COLLAPSED} more`}
            </button>
          )}
          {resolved.length > 0 && (
            <button type="button" onClick={() => setShowResolved(!showResolved)} style={alertToggle(WARN.strong)}>
              {showResolved ? 'Hide resolved' : `${resolved.length} resolved`}
            </button>
          )}
        </div>
      )}
    </section>
  );
}

// ---- Tab --------------------------------------------------------------------------------------------

export const EnergyTab: React.FC = (): React.ReactElement => {
  const mock = useMemo(mockConfig, []);
  const clock = () => Date.now() + (mock?.clockOffsetMs ?? 0);
  const mockPlan = useMemo(() => {
    if (!mock) {
      return undefined;
    }
    const s = energyMockSnapshot(clock());
    return mock.legacy ? legacySnapshot(s) : s;
  }, [mock]);
  const [plan, setPlan] = useState<EnergyPlanSnapshot | undefined>(mockPlan);
  const [loaded, setLoaded] = useState(mockPlan !== undefined);
  const [now, setNow] = useState(clock);
  const [live, setLive] = useState<SolarState | undefined>(undefined);
  const width = useViewportWidth();
  const size = layoutFor(width);

  useInterval(async () => {
    setNow(clock());
    if (mock) {
      return;
    }
    try {
      const data = await fetch('/energy/plan');
      const dataJSON: EnergyPlanSnapshot = JSON.parse(await data.text());
      setPlan(Array.isArray(dataJSON.slots) ? dataJSON : undefined);
    } catch (e) {
      console.log('ERROR:');
      console.log(e);
    } finally {
      setLoaded(true);
    }
  }, 60_000);

  // Live power every 10 s (the Pi posts it to /solar/state at that rate).
  useInterval(async () => {
    if (mock) {
      setLive(mock.legacy || !mockPlan ? undefined : energyMockSolarState(mockPlan, clock()));
      return;
    }
    try {
      const data = await fetch('/solar/state');
      const state: SolarState = JSON.parse(await data.text());
      setLive(state.consumePowerWatts === undefined ? undefined : state);
    } catch (e) {
      console.log('ERROR:');
      console.log(e);
    }
  }, 10_000);

  const rows = useMemo(() => (plan ? buildRows(plan) : []), [plan]);
  const runs = useMemo(() => buildRuns(rows), [rows]);
  const flow = plan ? flowNow(plan, live, now) : undefined;

  const stale = plan !== undefined && now - plan.createdAt > PLAN_STALE_AFTER_MS;
  const age = plan ? formatRelative(plan.createdAt, now) : '';
  const made = plan ? hhmm(plan.createdAt) : '';
  const modes = plan ? `battery ${plan.mode} · vent ${plan.ventilationMode}` : '';
  const cfg = plan?.configVersion?.slice(0, 8);
  const isLive = flow?.source === 'live';
  const status = useMemo(
    () =>
      plan ? <HeaderStatus size={size} stale={stale} text={statusText(stale, isLive, age, made, modes, cfg)} /> : null,
    [plan !== undefined, size, stale, isLive, age, made, modes, cfg],
  );
  useHeaderStatus(status);

  const phone = size === 'phone';
  const web = size === 'web';
  const page = pageStyle(size);

  if (!plan) {
    return (
      <div style={page}>
        <Alert variant="info" className="mb-0">
          {loaded
            ? 'No energy plan received yet. The Pi posts one to /energy/plan after every replan (add ?mock=energy to the URL for sample data).'
            : 'Loading energy plan…'}
        </Alert>
      </div>
    );
  }

  const rooms = airRooms(plan, now);
  const ventLevel = ventLevelNow(plan, now);
  const vent = ventLevel ? VENT_NAMES[ventLevel] : undefined;
  const hasAirBlock = rooms.length > 0 || vent !== undefined;
  const items = upcomingRuns(runs, now);
  const flowCard = (style: React.CSSProperties) => (
    <PowerFlowCard plan={plan} runs={runs} flow={flow} now={now} statSize={size === 'tablet' ? 16 : 15} style={style} />
  );
  const timeline = (
    <TimelineCard
      plan={plan}
      rows={rows}
      now={now}
      size={size}
      style={{ padding: phone ? '14px 12px 12px' : web ? '16px 20px 14px' : '16px 18px 14px' }}
    />
  );
  const footer = <Footer plan={plan} size={size} modes={modes} />;
  const alerts = <AlertsBanner plan={plan} now={now} />;

  if (phone) {
    return (
      <div style={page}>
        {alerts}
        {flowCard({ padding: '12px 14px' })}
        <CostCard plan={plan} size={size} />
        <NoExportBanner plan={plan} now={now} size={size} />
        {hasAirBlock && <AirChips rooms={rooms} vent={vent} />}
        {timeline}
        {items.length > 0 && <ComingUpList items={items} size={size} />}
        {footer}
      </div>
    );
  }

  const side = (
    <div
      style={{ display: 'flex', flexDirection: 'column', gap: 12, minWidth: 0, flex: web ? '1 1 310px' : undefined }}
    >
      <CostCard plan={plan} size={size} />
      <NoExportBanner plan={plan} now={now} size={size} />
      {hasAirBlock && <AirCard rooms={rooms} vent={vent} />}
    </div>
  );

  if (!web) {
    return (
      <div style={page}>
        {alerts}
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: width >= 760 ? 'minmax(0, 1.5fr) minmax(0, 1fr)' : 'minmax(0, 1fr)',
            gap: 16,
          }}
        >
          {flowCard({ padding: '14px 18px' })}
          {side}
        </div>
        {timeline}
        {items.length > 0 && <ComingUpCards items={items} />}
        {footer}
      </div>
    );
  }

  return (
    <div style={page}>
      {alerts}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16, alignItems: 'stretch' }}>
        {flowCard({ flex: '0 1 400px', minWidth: 0, boxSizing: 'border-box', padding: '14px 16px' })}
        {side}
        {items.length > 0 && (
          <ComingUpList
            items={items}
            size={size}
            scrollBeside
            width={width}
            style={{ flex: '1 1 310px', minWidth: 0 }}
          />
        )}
      </div>
      {timeline}
      {footer}
    </div>
  );
};
